import clsx from 'clsx';
import { type InputHTMLAttributes, type SVGProps, useState } from 'react';
import { EyeIcon, EyeOffIcon } from '../Icons';
type InputSize = 'small' | 'medium' | 'large';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
	prefixIcon?: React.ComponentType<SVGProps<SVGSVGElement>>;
	suffixIcon?: React.ComponentType<SVGProps<SVGSVGElement>>;
	placeholder?: string;
	inputSize?: InputSize;
	type?: 'text' | 'password';
	error?: string;
	label?: React.ReactNode;
	allowClear?: boolean;
	className?: string;
	inputClassName?: string;
}

// adjust size if necessary, only medium size is correct
const INPUT_HEIGHT = {
	small: '2rem',
	medium: '3.25rem',
	large: '4rem',
};

export default function Input(props: InputProps) {
	const {
		prefixIcon: PrefixIcon,
		suffixIcon: SuffixIcon,
		label,
		placeholder,
		inputSize = 'medium',
		type = 'text',
		error,
		allowClear = false,
		onChange,
		value,
		className,
		inputClassName,
		...restProps
	} = props;

	const [showPassword, setShowPassword] = useState(false);

	const handleTogglePassword = () => {
		setShowPassword(!showPassword);
	};

	const handleClear = () => {
		if (onChange) {
			const event = {
				...new Event('change'),
				target: { value: '' },
			} as unknown as React.ChangeEvent<HTMLInputElement>;
			onChange(event);
		}
	};

	const renderSuffixIcon = () => {
		if (type === 'password') {
			return (
				<button
					type="button"
					onClick={handleTogglePassword}
					className="focus:outline-none"
				>
					{showPassword ? (
						<EyeIcon className="w-5 h-5 text-grey-2" />
					) : (
						<EyeOffIcon className="w-5 h-5 text-grey-2" />
					)}
				</button>
			);
		}
		if (SuffixIcon) {
			return <SuffixIcon className="text-grey-2" />;
		}
		return null;
	};

	return (
		<div className={clsx('w-full', className)}>
			<div
				className={clsx(
					'mb-2 text-sm font-medium',
					error ? 'text-red-500' : 'text-secondary-black',
				)}
			>
				{label}
			</div>
			<div className="relative flex items-center w-full">
				{PrefixIcon && (
					<div className="absolute left-4">
						<PrefixIcon
							className={clsx(error ? 'text-red-500' : 'text-grey-2')}
						/>
					</div>
				)}
				<input
					value={value}
					onChange={onChange}
					style={{ height: INPUT_HEIGHT[inputSize] }}
					className={clsx(
						'w-full rounded-full font-medium text-secondary-black text-sm outline-none border placeholder:text-grey-2',
						error
							? 'border-red-500 focus:border-red-500'
							: 'border-grey-3 focus:border-green',
						PrefixIcon ? 'pl-11' : 'pl-4',
						SuffixIcon ? 'pr-10' : 'pr-4',
						props.disabled
							? 'bg-grey-4 border-grey-4 cursor-not-allowed'
							: 'bg-white',
						inputClassName,
					)}
					placeholder={placeholder}
					type={
						type === 'password' ? (showPassword ? 'text' : 'password') : type
					}
					{...restProps}
				/>
				{(type === 'password' || SuffixIcon) && (
					<div className="absolute right-4">{renderSuffixIcon()}</div>
				)}
				{allowClear && value && type !== 'password' && (
					<div className="absolute right-4">
						<button
							type="button"
							onClick={handleClear}
							className="focus:outline-none"
						>
							X
						</button>
					</div>
				)}
			</div>
			{error && <p className="mt-1 text-sm text-red-500">{error}</p>}
		</div>
	);
}
